import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, X, Filter, Calendar, Users, FileSpreadsheet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable export dropdown button. Place it anywhere to add Excel export.
 * Props:
 *  - type: 'expenses' | 'trips' | 'person-wise' | 'analytics' | 'trip-detail' | 'zip'
 *  - fixedFilters: { trip_id, employee_id, approval_status, etc } - pre-set filters
 *  - showFilterUI: boolean - show date/employee picker
 *  - label: custom button label
 *  - size: 'sm' | 'md'
 */
export const ExportButton = ({ type = 'expenses', fixedFilters = {}, showFilterUI = false, label, size = 'sm', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  useEffect(() => {
    if (showFilterUI && open) {
      fetchUsers();
    }
  }, [open, showFilterUI]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data.filter(u => u.role === 'bdm' && u.status === 'active'));
    } catch { /* ignore */ }
  };

  const getEndpoint = () => {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (selectedEmployee) params.set('employee_id', selectedEmployee);
    Object.entries(fixedFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    const qs = params.toString() ? `?${params.toString()}` : '';

    switch (type) {
      case 'expenses': return `/export/expenses/excel${qs}`;
      case 'trips': return `/export/trips/excel${qs}`;
      case 'person-wise': return `/export/person-wise/excel${qs}`;
      case 'analytics': return `/export/analytics/excel${qs}`;
      case 'trip-detail': return `/export/trips/${fixedFilters.trip_id}/excel`;
      case 'zip': return `/export/expenses/zip${qs}`;
      default: return `/export/expenses/excel${qs}`;
    }
  };

  const getFilename = () => {
    const d = new Date().toISOString().split('T')[0];
    switch (type) {
      case 'expenses': return `expenses_${d}.xlsx`;
      case 'trips': return `trips_${d}.xlsx`;
      case 'person-wise': return `person_wise_${d}.xlsx`;
      case 'analytics': return `analytics_${d}.xlsx`;
      case 'trip-detail': return `trip_export_${d}.xlsx`;
      case 'zip': return `expense_export_${d}.zip`;
      default: return `export_${d}.xlsx`;
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}${getEndpoint()}`, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = getFilename();
      link.click();
      URL.revokeObjectURL(link.href);
      setOpen(false);
    } catch (err) {
      alert('Export failed: ' + (err.message || 'Unknown error'));
    } finally { setLoading(false); }
  };

  const btnSize = size === 'sm' ? 'text-xs py-1.5 px-3' : 'text-sm py-2 px-4';

  // Simple export (no filter UI)
  if (!showFilterUI) {
    return (
      <button onClick={handleExport} disabled={loading}
        className={`btn btn-secondary ${btnSize} ${className}`}
        data-testid={`export-${type}-btn`}>
        <Download className="w-3.5 h-3.5" />
        {loading ? 'Exporting...' : (label || 'Export Excel')}
      </button>
    );
  }

  // With filter dropdown
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className={`btn btn-secondary ${btnSize} ${className}`}
        data-testid={`export-${type}-btn`}>
        <Download className="w-3.5 h-3.5" />
        {label || 'Export Excel'}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-40" data-testid="export-filter-dropdown">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-semibold text-gray-700">Export Filters</span>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  <Calendar className="w-3 h-3 inline mr-1" />Date Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    className="input-field text-xs" placeholder="From" data-testid="export-start-date" />
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                    className="input-field text-xs" placeholder="To" data-testid="export-end-date" />
                </div>
              </div>

              {type !== 'trip-detail' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    <Users className="w-3 h-3 inline mr-1" />BDM / Employee
                  </label>
                  <select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)}
                    className="input-field text-xs" data-testid="export-employee-select">
                    <option value="">All Employees</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              )}

              <button onClick={handleExport} disabled={loading}
                className="btn btn-primary w-full justify-center text-sm"
                data-testid="export-download-btn">
                <FileSpreadsheet className="w-4 h-4" />
                {loading ? 'Generating...' : 'Download Excel'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ExportButton;
